import { refreshStatuses, refreshStatusNames } from '../enums/store-refresh-status';
import { finalStoreRefreshStatuses } from './store-refresh-service';

type ProgressFields = {
  ordersReceivedCount: number;
  ordersImportedCount: number;
  newOrders: number;
  totalOrders: number;
  progress: number;
  importLabel: string | null;
};

type StoreData = {
  Status: number;
  StoreGUID: string;
  Label?: string | null;
  OrdersCount?: number | null;
  NewOrders?: number | null;
  TotalOrders?: number | null;
  TotalPageCount?: number | null;
  Progress?: number | null;
};

type ExistingStore = {
  ordersReceivedCount?: number;
  ordersImportedCount?: number;
  newOrders?: number;
  totalOrders?: number;
  importLabel?: string | null;
  [key: string]: unknown;
};

// Per-store progress is persisted so counts survive Pusher reconnects mid-import.
// Cleared on Queued (new import starts) and on terminal states (Idle/Error/99).
// If an import is interrupted (crash, tab close), the stale entry lingers until
// the next Queued or terminal event — worst case is briefly showing old counts
// before the following Pusher message corrects them.
const storageKey = (storeGuid: string) => `shopify_progress_${storeGuid}`;

const saveProgress = (storeGuid: string, fields: ProgressFields) => {
  try {
    localStorage.setItem(storageKey(storeGuid), JSON.stringify(fields));
  } catch {
    // localStorage unavailable — best effort
  }
};

const toSafeNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const loadProgress = (storeGuid: string): ProgressFields | null => {
  try {
    const raw = localStorage.getItem(storageKey(storeGuid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      ordersReceivedCount: toSafeNumber(parsed?.ordersReceivedCount),
      ordersImportedCount: toSafeNumber(parsed?.ordersImportedCount),
      newOrders: toSafeNumber(parsed?.newOrders),
      totalOrders: toSafeNumber(parsed?.totalOrders),
      progress: toSafeNumber(parsed?.progress),
      importLabel: parsed?.importLabel ?? null,
    };
  } catch {
    return null;
  }
};

const clearProgress = (storeGuid: string) => {
  try {
    localStorage.removeItem(storageKey(storeGuid));
  } catch {
    // best effort
  }
};

export const enrichShopifyStoreFields = (storeData: StoreData, existingStore: ExistingStore) => {
  const isTerminal = finalStoreRefreshStatuses.includes(storeData.Status);
  const isQueued = storeData.Status === 1;
  const storeGuid = storeData.StoreGUID;

  if (isQueued || isTerminal) {
    clearProgress(storeGuid);
  }

  const saved = !isQueued && !isTerminal ? loadProgress(storeGuid) : null;

  const getOrdersCount = (label: 'ordersReceivedCount' | 'ordersImportedCount'): number => {
    if (isQueued) return 0;
    if (storeData.Label === label) return storeData.OrdersCount ?? 0;
    return toSafeNumber(existingStore[label] ?? saved?.[label] ?? 0);
  };

  const ordersReceivedCount = getOrdersCount('ordersReceivedCount');
  const ordersImportedCount = getOrdersCount('ordersImportedCount');

  let newOrders = 0;
  if (!isQueued) {
    newOrders =
      storeData.Label === 'ordersImportedCount'
        ? toSafeNumber(storeData.NewOrders ?? existingStore.newOrders ?? saved?.newOrders ?? 0)
        : toSafeNumber(existingStore.newOrders ?? saved?.newOrders ?? 0);
  }

  const totalOrders = isQueued ? 0 : toSafeNumber(storeData.TotalOrders ?? existingStore.totalOrders ?? saved?.totalOrders ?? 0);

  const progress = isTerminal ? 0 : toSafeNumber(storeData.Progress ?? saved?.progress ?? 0);
  const importLabel = isQueued || isTerminal ? null : (storeData.Label ?? existingStore.importLabel ?? saved?.importLabel ?? null);

  if (!isQueued && !isTerminal) {
    saveProgress(storeGuid, { ordersReceivedCount, ordersImportedCount, newOrders, totalOrders, progress, importLabel });
  }

  return {
    progress,
    totalOrders,
    ordersReceivedCount,
    ordersImportedCount,
    newOrders,
    importLabel,
    refreshStatus: refreshStatuses[storeData.Status as keyof typeof refreshStatuses] ?? refreshStatusNames.idle,
  };
};
